import {
  assert_array_not_empty,
  assert_hash_sha256_not_empty,
  assert_number_gt_0,
  assert_string_not_empty,
  assert_type_enum,
} from '@shared/asserts/utils_asserts';
import { assert_object_param_enum, assert_object_param_string } from '@shared/asserts/utils_asserts_object';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { ENUM__string_id_empty } from '@shared/constants/ENUM__string_id_empty';
import { DTO__database_entity } from '@shared/dto/_database_entity/DTO__database_entity';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { logw } from '@shared/utils/utils_logs';
import { ENUM__float_point_precision } from '../_enums/ENUM__float_point_precision';
import { ENUM__ml_asset_family } from '../_enums/ENUM__ml_asset_family';
import { ENUM__ml_asset_status } from '../_enums/ENUM__ml_asset_status';
import { ENUM__ml_asset_type } from '../_enums/ENUM__ml_asset_type';
import { ENUM__ml_asset_url_source } from '../_enums/ENUM__ml_asset_url_source';
import { DTO__ml_asset_config } from './DTO__ml_asset_config';

//todo: add fields
//nsfw
//min image size
//max image size

const { URL_SOURCE_DELIMETER } = CONSTANTS__shared;

// todo: implement rent_price
export class DTO__ml_asset extends DTO__database_entity {
  /*
   * STATIC FIELDS
   */
  static _class_name_static: string = ENUM__dto_class_name.ML_ASSET;

  /*
   * STATIC FIELDS DESERIALIZATION
   */

  static readonly FIELD_NAME_TYPE: string = 'type';
  static readonly FIELD_NAME_FAMILY = 'family';

  static readonly FIELD_NAME_NAME = 'name';
  static readonly FIELD_NAME_VERSION = 'version';

  static readonly FIELD_NAME_URL_SOURCE = 'url_source';
  static readonly FIELD_NAME_URL_SOURCE_TYPE = 'url_source_type';

  static readonly FIELD_NAME_STATUS = 'status';

  static readonly FIELD_NAME_HASH_SHA256 = 'hash_sha256';

  static readonly FIELD_NAME_FLOAT_PRECISION = 'float_precision';
  static readonly FIELD_NAME_CONFIG_STRING = 'config_string';

  /*
   * STATIC METHODS
   */

  static sort_by_name_and_version(item_a: DTO__ml_asset, item_b: DTO__ml_asset): number {
    let [name_a, version_a] = [item_a.name.only_alphanumeric().toLowerCase(), item_a.version.toLowerCase()];
    let [name_b, version_b] = [item_b.name.only_alphanumeric().toLowerCase(), item_b.version.toLowerCase()];

    let out: number;

    if (name_a == name_b) {
      out = version_a.localeCompare(version_b);
    } else {
      out = name_a.localeCompare(name_b);
    }

    return out;
  }

  static sort_by_url_source(a: DTO__ml_asset, b: DTO__ml_asset) {
    return a.url_source.toLowerCase().localeCompare(b.url_source.toLowerCase());
  }

  static map_by_hash(items: DTO__ml_asset[]) {
    let out = new Map<string, DTO__ml_asset>();

    for (let item of items) {
      assert_hash_sha256_not_empty(item.hash_sha256);

      out.set(item.hash_sha256, item);
    }

    return out;
  }

  /*
   * PROPERTIES
   */

  get config() {
    let out: DTO__ml_asset_config = new DTO__ml_asset_config();

    out.config_string = this.config_string;
    out.ml_asset_type = this.type;

    return out;
  }

  /*
   * FIELDS
   */

  type!: ENUM__ml_asset_type;
  family!: ENUM__ml_asset_family;

  hash_sha256!: string;
  hash_sha256_children_list: string[];

  name!: string;
  version!: string;

  size_disk!: number;

  url_source!: string;
  url_source_type!: ENUM__ml_asset_url_source;

  status!: ENUM__ml_asset_status;

  float_precision!: ENUM__float_point_precision;
  config_string!: string;

  /*
   * PROPERTIES
   */

  /*
   * METHODS
   */

  constructor() {
    super();

    this.hash_sha256_children_list = [];
  }

  private try_repack_url_source() {
    if (this.url_source_type != ENUM__ml_asset_url_source.MULTIPLE_FILES) return;

    let urls = this.url_source.split(URL_SOURCE_DELIMETER);

    if (urls.length < 2) logw(`url_source contains less than 2 urls with type ${this.url_source_type}`);

    urls.sort();

    this.url_source = urls.join(URL_SOURCE_DELIMETER);
  }

  deserialize(data: object): void {
    /*
     * FIELDS INIT
     */
    this.status = ENUM__ml_asset_status.DOWNLOAD_NOT_STARTED;
    this.size_disk = 0;
    this.hash_sha256 = ENUM__string_id_empty.HASH_SHA256;

    /*
     * FIELDS DESERIALIZE
     */

    this.type = assert_object_param_enum(data, DTO__ml_asset.FIELD_NAME_TYPE, ENUM__ml_asset_type);
    this.family = assert_object_param_enum(data, DTO__ml_asset.FIELD_NAME_FAMILY, ENUM__ml_asset_family);

    this.name = assert_object_param_string(data, DTO__ml_asset.FIELD_NAME_NAME);
    this.version = assert_object_param_string(data, DTO__ml_asset.FIELD_NAME_VERSION);

    if (this.version.is_blank()) this.version = ENUM__string_id_empty.ML_ASSET_VERSION;

    this.url_source = assert_object_param_string(data, DTO__ml_asset.FIELD_NAME_URL_SOURCE);
    this.url_source_type = assert_object_param_enum(data, DTO__ml_asset.FIELD_NAME_URL_SOURCE_TYPE, ENUM__ml_asset_url_source);

    this.float_precision = assert_object_param_enum(data, DTO__ml_asset.FIELD_NAME_FLOAT_PRECISION, ENUM__float_point_precision);
    this.config_string = assert_object_param_string(data, DTO__ml_asset.FIELD_NAME_CONFIG_STRING);

    this.try_repack_url_source();
  }

  assert_standard_fields(): void {
    if (this.is_stub()) return;

    super.assert_standard_fields();

    assert_type_enum(this.type, ENUM__ml_asset_type);
    assert_type_enum(this.family, ENUM__ml_asset_family);

    assert_string_not_empty(this.name);
    assert_string_not_empty(this.version);

    assert_string_not_empty(this.hash_sha256);

    assert_string_not_empty(this.url_source);
    assert_type_enum(this.url_source_type, ENUM__ml_asset_url_source);

    assert_type_enum(this.status, ENUM__ml_asset_status);

    assert_type_enum(this.float_precision, ENUM__float_point_precision);

    this.try_assert_standard_fields_downloaded();

    this.try_repack_url_source();

    this.hash_sha256_children_list = this.hash_sha256_children_list.unique_values();
    this.hash_sha256_children_list.sort();
  }

  private try_assert_standard_fields_downloaded() {
    if (this.status != ENUM__ml_asset_status.DOWNLOAD_SUCCESS) return;

    assert_string_not_empty(this.hash_sha256);

    if (this.type == ENUM__ml_asset_type.BUNDLE) assert_array_not_empty(this.hash_sha256_children_list);

    assert_number_gt_0(this.size_disk);
  }
}
